<template lang='pug'>
  .brands-index
    template(v-if='isMobile')
      ol.alphabet-list-mobile(:class='{ "with-offline-banner": isOffline }')
        li.brand-letter-scroll(
          :class='{ "highlightedList": currentScrolledLetter === row.enLetter }'
          v-for='row in brandsInRows'
          v-if='brandList(row.currentLangLetter).length > 0'
          @touchstart='setCurrentLetter(row.enLetter)'
          v-text='row.currentLangLetter'
          :key='row.enLetter'
        )
      ol
        li(
          v-for='row in brandsInRows'
          v-if='brandList(row.currentLangLetter).length > 0'
          :ref='`brand-letter-${row.enLetter}`'
          :key='row.enLetter'
        )
          .brand-list-mobile(v-text='row.currentLangLetter')
          ol
            li.brand-list-letter-mobile(v-for='brand in brandList(row.currentLangLetter)')
              router-link(:to='brandPath(brand)' v-text='brand.attributes.name')

    template(v-else)
      .brand-listing-page
        h1.header(v-t='"products.brand.title"')
        div(:class='{ "scroll-helper": scrollHelper }')
        ol.alphabet-list(:class='{ "sticky": stickyBrandsLetters }')
          li.alphabet(
            :class='{ "highlightedList": currentScrolledLetter === row.enLetter }'
            v-for='row in brandsInRows'
            v-if='brandList(row.currentLangLetter).length > 0'
            @click='setCurrentLetter(row.enLetter)'
            v-text='row.currentLangLetter'
            :key='row.enLetter'
          )
        .separator
        ol.brand-list
          li(
            v-for='row in brandsInRows'
            v-if='brandList(row.currentLangLetter).length > 0'
            :ref='`brand-letter-${row.enLetter}`'
            :key='row.enLetter'
            :id='`letter-${row.enLetter}`'
          )
            table
              tr.brand-by-alphabet(:id='row.enLetter')
                td.alphabet.left
                  .alphabet-text(:class='{ "highlight": highlightLetter === row.enLetter }')
                    p.letter(v-text='row.currentLangLetter')
                td.brand-name(v-for='column in row.brands')
                  li(v-for='brand in column')
                    router-link(:to='brandPath(brand)' v-text='brand.attributes.name')
                td.alphabet.right
                  .alphabet-text(:class='{ "highlight": highlightLetter === row.enLetter }')
                    p.letter(v-text='row.currentLangLetter')
</template>

<script>
import { mapState, mapGetters } from 'vuex'
import chunk from 'lodash/chunk'
import { disassemble } from 'hangul-js'
import { pushDataLayerEvent } from '@/modules/services/gtm'
import { brandUtil } from '@/mixins/pathUtil'
import { dyUtil } from '@/mixins/dyUtil'
import { fullUrl } from '@/modules/services/utilities'
import { localeIsKR, allLetters } from '@/modules/services/lettersForLanguage'

export default {
  mixins: [
    brandUtil,
    dyUtil
  ],

  data () {
    return {
      brandColumns: 3,
      stickyBrandsLetters: false,
      currentLetter: '',
      currentLetterTemp: 'a',
      currentScrolledLetter: 'a',
      highlightLetter: '',
      lastScroll: 0,
      scrollHelper: false,
      scrollCheck: true,
      scrolledLetterOffset: 120,
      mobileAppBannerOffset: 88
    }
  },

  computed: {
    ...mapState({
      brands: state => state.brands.allBrands,
      brandsLoaded: state => state.brands.brandsLoaded,
      isOffline: state => state.layout.isOffline
    }),
    ...mapGetters([
      'brandsStartingWithInitialChar',
      'brandsStartingWithNumber'
    ]),
    isMobile: () => vueSupport.isMobile,
    brandsInRows () {
      const brandsInRows = []

      allLetters().forEach((letter) => {
        const brands = this.brandList(letter.currentLangLetter)
        const numberOfRows = Math.ceil(brands.length / this.brandColumns)
        const chunkedBrands = chunk(brands, numberOfRows)
        const brandsForLetter = {}
        // fill array up to three columns to ensure table is not broken

        brandsForLetter.currentLangLetter = letter.currentLangLetter
        brandsForLetter.enLetter = letter.enLetter
        brandsForLetter.brands = chunkedBrands.concat(Array(this.brandColumns - chunkedBrands.length).fill([]))

        brandsInRows.push(brandsForLetter)
      })

      return brandsInRows
    }
  },

  watch: {
    currentLetter () {
      this.scrollToLetter()
    }
  },

  created () {
    window.addEventListener('scroll', this.onScrollDesktop)
    window.addEventListener('scroll', this.highlightLetterOnScroll)
    window.addEventListener('touchmove', this.onTouchmoveMobile, { passive: false })

    this.loadBrands()
  },

  mounted () {
    this.$store.commit('setSeoMetaTitle', 'Brands')
    this.$store.commit('setSeoMetaDescription', this.$t('meta.description', I18n.siteCountryLs))
    this.$store.commit('setSeoOgUrl', fullUrl(this.$route.path))
    this.$store.commit('setSeoUpdate', true)
    this.initDY('OTHER')
  },

  destroyed () {
    window.removeEventListener('scroll', this.onScrollDesktop)
    window.removeEventListener('scroll', this.highlightLetterOnScroll)
    window.removeEventListener('touchmove', this.onTouchmoveMobile)
  },

  methods: {
    async loadBrands () {
      if (!this.brandsLoaded) {
        try {
          await this.$store.dispatch('fetchBrands')
          this.$nextTick(() => {
            pushDataLayerEvent('DynamicWindowLoad')
          })
        } catch (error) {}
      } else {
        this.$nextTick(() => {
          pushDataLayerEvent('DynamicWindowLoad')
        })
      }
    },

    brandList (letter) {
      const initialChar = 'a'

      if (localeIsKR()) {
        // ex. '가' -> 'ㄱ
        letter = disassemble(letter)[0]
        return this.brandsStartingWithInitialChar(letter)
      } else if (letter.toLowerCase() === initialChar) {
        return this.brandsStartingWithNumber.concat(this.brandsStartingWithInitialChar(letter))
      } else {
        return this.brandsStartingWithInitialChar(letter)
      }
    },

    onScrollDesktop () {
      if (this.isMobile) {
        return
      }

      const navOffsetTop = 170
      const topbarHeight = 35
      const sitewideBannersHeight = $('.js-header-banner').height() + $('.js-sign-in-banner').height()

      if (window.pageYOffset > navOffsetTop + sitewideBannersHeight - topbarHeight) {
        this.stickyBrandsLetters = true
        this.scrollHelper = true
      } else {
        this.stickyBrandsLetters = false
        this.scrollHelper = false
      }
    },

    highlightLetterOnScroll () {
      const currentScrolledLetterOffset = this.scrolledLetterOffset

      if ((this.lastScroll - window.pageYOffset) < 0) {
        if (this.$refs[`brand-letter-${this.nextScrolledLetter()}`] && this.$refs[`brand-letter-${this.nextScrolledLetter()}`][0].getBoundingClientRect().top < currentScrolledLetterOffset + 1) {
          if (!this.scrollCheck) {
            this.currentScrolledLetter = this.currentLetter
            this.scrollCheck = true
          } else {
            this.currentScrolledLetter = this.nextScrolledLetter()
            this.currentLetterTemp = this.currentScrolledLetter
          }
          if (this.currentScrolledLetter !== this.highlightLetter) {
            this.highlightLetter = ''
          }
        }
      } else {
        if (this.$refs[`brand-letter-${this.prevScrolledLetter()}`] && this.$refs[`brand-letter-${this.prevScrolledLetter()}`][0].getBoundingClientRect().bottom > currentScrolledLetterOffset) {
          if (!this.scrollCheck) {
            this.currentScrolledLetter = this.currentLetter
            this.scrollCheck = true
          } else {
            this.currentScrolledLetter = this.prevScrolledLetter()
            this.currentLetterTemp = this.currentScrolledLetter
          }
          if (this.currentScrolledLetter !== this.highlightLetter) {
            this.highlightLetter = ''
          }
        }
      }
      this.lastScroll = window.pageYOffset
    },

    setCurrentLetter (letter) {
      this.currentLetter = letter
    },

    onTouchmoveMobile (e) {
      if (e.srcElement.className.includes('brand-letter-scroll')) {
        e.preventDefault()
        const target = document.elementFromPoint(e.changedTouches[0].clientX, e.changedTouches[0].clientY)
        if (target.className === 'brand-letter-scroll') {
          this.currentLetter = target.textContent.toLowerCase()
        }
      }
    },

    scrollToLetter () {
      if (this.isMobile) {
        const top = this.$refs[`brand-letter-${this.currentLetter}`][0].getBoundingClientRect().top + window.pageYOffset
        const offset = top - this.scrolledLetterOffset

        window.scroll(0, offset)
        this.currentScrolledLetter = this.currentLetter
        this.currentLetterTemp = this.currentLetter
      } else {
        this.$scrollTo(`#letter-${this.currentLetter}`, 500, {
          offset: -115,
          onDone: () => {
            this.currentScrolledLetter = this.currentLetter
            this.highlightLetter = this.currentLetter
            this.scrollCheck = false
          }
        })
      }
    },

    nextScrolledLetter () {
      const nextChar = String.fromCharCode(this.currentLetterTemp.charCodeAt(0) + 1)
      if (this.$refs[`brand-letter-${nextChar}`]) {
        return nextChar
      } else {
        this.currentLetterTemp = nextChar
        if (this.currentLetterTemp.match(/[a-z]/i)) {
          return this.nextScrolledLetter()
        }
      }
    },

    prevScrolledLetter () {
      const prevChar = String.fromCharCode(this.currentLetterTemp.charCodeAt(0) - 1)
      if (this.$refs[`brand-letter-${prevChar}`]) {
        return prevChar
      } else {
        this.currentLetterTemp = prevChar
        if (this.currentLetterTemp.match(/[a-z]/i)) {
          return this.prevScrolledLetter()
        }
      }
    }
  }
}
</script>

<style lang='scss' scoped>
  .brands-index {
    margin-left: -9px;
    margin-right: -9px;

    @include media-breakpoint-up(sm) {
      margin-left: 2px;
      margin-right: 2px;
    }

    @include media-breakpoint-up(xl) {
      margin-left: -15px;
      margin-right: -15px;
    }
  }

  .alphabet-list-mobile {
    @include ease-in-out-quad-transition;
    position: fixed;
    background-color: white;
    height: calc(100% - 122px);
    top: 122px;
    right: 0;
    display: flex;
    flex-direction: column;
    justify-content: space-around;
    align-items: center;

    &.with-offline-banner {
      height: calc(100% - 122px - #{$offline-banner-height}); // resize letter list height to fit all letters
      top: 122px + $offline-banner-height;
    }

    li {
      padding-right: 15px;
      padding-left: 15px;
      @include font-weight-book;
      font-size: 12px;
      text-transform: uppercase;
      height: 3.7%;
    }
  }

  .brand-list-mobile {
    @include font-weight-bold;
    font-size: 18px;
    padding-bottom: 8px;
    padding-top: 8px;
    padding-left: 10px;
    background-color: $grey-light;
    text-transform: uppercase;
  }

  .brand-list-letter-mobile {
    border-bottom: 1px solid $grey;
    margin-left: 13px;

    a {
      display: block;
      padding-top: 15px;
      padding-bottom: 15px;
    }
  }

  .brand-list-letter-mobile:last-child {
    border-bottom: none;
  }

  .scroll-helper {
    height: 40px;
  }

  .header {
    padding: 14px 85px 11px 85px;
    border-bottom: 4px solid;
    text-transform: uppercase;
  }

  .alphabet-list {
    padding: 11px 85px;
    margin: 0 auto;
    display: flex;
    justify-content: center;
    box-sizing: border-box;

    @include media-breakpoint-up(xl) {
      .alphabet-list > .alphabet:hover {
        color: $brand-primary;
      }
    }

    &.sticky {
      background: $white;
      position: fixed;
      top: 70px; // 1px for border
      z-index: 100;
      left: 50%;
      transform: translateX(-50%);
      width: 100vw;
      height: 45px;
      border-top: 1px solid $grey;
      border-bottom: 1px solid $black;

      @include media-breakpoint-up(xl) {
        width: 1155px;
      }
    }

    .alphabet {
      cursor: pointer;
      float: left;
      @include font-weight-book;
      font-size: 18px;
      line-height: 18px;
      padding-right: 6px;
      padding-left: 6px;
      text-transform: uppercase;

      @include media-breakpoint-up(md) {
        padding-right: 10px;
        padding-left: 10px;
      }

      @include media-breakpoint-up(xl) {
        padding-right: 14px;
        padding-left: 14px;
      }
    }
  }

  .separator {
    border-top: 1px solid;
  }

  table {
    width: 100%;
    padding: 5px 87px 5px 102px;
  }

  .brand-by-alphabet {
    border-bottom: 1px solid $black;
  }

  #z {
    border-bottom: none;
  }

  .alphabet {
    width: 80px;
    text-align: center;
    display: table-cell;

    @include media-breakpoint-up(md) {
      width: 100px;
    }

    @include media-breakpoint-up(xl) {
      width: 130px;
    }
  }

  .alphabet-text {
    display: inline-block;
    vertical-align: middle;
    @include font-weight-bold;
    font-size: 24px;
    width: 40px;
    height: 40px;
    border-radius: 50%;
    text-transform: uppercase;
  }

  .letter {
    margin-top: 13.5px;
  }

  .left {
    padding-left: 30px;
    padding-right: 30px;

    @include media-breakpoint-up(xl) {
      padding-left: 70px;
    }

  }
  .right {
    padding-right: 30px;

    @include media-breakpoint-up(xl) {
      padding-right: 70px;
    }
  }

  .brand-name {
    width: 115px;
    padding-right: 25px;
    min-height: 36px;
    @include font-weight-book;
    font-size: 13px;
    line-height: 13px;
    vertical-align: top;
    padding-top: 5px;
    padding-bottom: 5px;

    @include media-breakpoint-up(md) {
      width: 190px;
    }

    @include media-breakpoint-up(xl) {
      width: 180px;
    }

    :last-child {
      line-height: 11px;
    }

    li {
      margin-top: 3px;
      margin-bottom: 3px;
    }
  }

  .highlightedList {
    @include font-weight-medium;
    color: $brand-primary;
  }

  .highlight {
    background-color: $brand-primary;
    color: $white;
  }
</style>
